import React, { useRef, useEffect, useState, useCallback } from 'react';
import type { Point, Selection } from '../types';
import type { ToolMode } from '../types';

interface ImageEditorProps {
  imageSrc: string;
  onLassoComplete: (points: Point[], dimensions: { width: number; height: number }) => void;
  onDrawingComplete: (imageDataUrl: string) => void;
  selection: Selection | null;
  toolMode: ToolMode;
  brushSize: number;
  brushColor: string;
}

export const ImageEditor: React.FC<ImageEditorProps> = ({
  imageSrc,
  onLassoComplete,
  onDrawingComplete,
  selection,
  toolMode,
  brushColor,
  brushSize,
}) => {
  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getBrushColor = () => hexToRgba(brushColor, 0.5); // 50% opacity

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cursorPos, setCursorPos] = useState<Point | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const lastPointRef = useRef<Point | null>(null);
  const [dashOffset, setDashOffset] = useState(0);

  // Marching ants animation
  useEffect(() => {
    let animationFrameId: number;
    const animate = () => {
      setDashOffset(prevOffset => (prevOffset - 0.5) % 20);
      animationFrameId = requestAnimationFrame(animate);
    };
    if (toolMode !== 'brush') {
      animationFrameId = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(animationFrameId);
  }, [toolMode]);

  // Handle 'Select All' mode
  useEffect(() => {
    if (toolMode === 'all') {
      const canvas = canvasRef.current;
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        const w = canvas.width;
        const h = canvas.height;
        const allPoints = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        // Trigger selection
        onLassoComplete(allPoints, { width: w, height: h });
      }
    }
  }, [toolMode, onLassoComplete]);

  // Sync parent selection (e.g., from restore) to local state
  useEffect(() => {
    if (toolMode !== 'brush') {
      const newPoints = selection?.points || [];
      setPoints(newPoints);

      // If selection is cleared externally (e.g. tool change), reset drawing state
      if (newPoints.length === 0) {
        setIsDrawing(false);
        setCursorPos(null);
      }
    }
  }, [selection, toolMode]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !image.naturalWidth) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    if (toolMode !== 'brush' && points.length > 0) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }

      const isClosed = !isDrawing && points.length > 2;

      if (isDrawing) {
        if (toolMode === 'polygonal' && cursorPos) {
          ctx.lineTo(cursorPos.x, cursorPos.y); // Draw preview line to cursor
        }
      } else if (isClosed) {
        ctx.closePath();
      }

      // --- Marching Ants Logic ---
      ctx.save();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.setLineDash([]);
      ctx.stroke();
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'white';
      ctx.setLineDash([8, 12]);
      ctx.lineDashOffset = dashOffset;
      ctx.stroke();
      ctx.restore();

      if (toolMode === 'polygonal' && points.length > 0) {
        const scaleFactor = Math.max(canvas.width, canvas.height) / 1000;
        const vertexRadius = Math.max(2, 3 * scaleFactor);
        points.forEach((p, index) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, vertexRadius, 0, 2 * Math.PI);
          ctx.fillStyle = (index === 0 && isDrawing) ? 'rgba(124, 58, 237, 1)' : 'rgba(255, 255, 255, 0.9)';
          ctx.fill();
        });
      }
    }
  }, [points, isDrawing, cursorPos, toolMode, dashOffset]);

  useEffect(() => {
    if (toolMode !== 'brush') {
      redrawCanvas();
    }
  }, [points, redrawCanvas, dashOffset, toolMode]);

  // Handle Escape key to cancel polygonal selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && toolMode === 'polygonal' && isDrawing) {
        setPoints([]);
        setIsDrawing(false);
        setCursorPos(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toolMode, isDrawing]);

  // Load image only when imageSrc changes
  useEffect(() => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      imageRef.current = image;
      setupCanvas(); // Initial setup
    };
  }, [imageSrc]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const image = imageRef.current;
    if (!canvas || !container || !image || !image.naturalWidth) return;

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const containerAspect = container.clientWidth / container.clientHeight;
    const imageAspect = image.naturalWidth / image.naturalHeight;

    if (imageAspect > containerAspect) {
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
    } else {
      canvas.style.width = 'auto';
      canvas.style.height = '100%';
    }
    redrawCanvas();
  }, [redrawCanvas]);

  // Handle container resizing
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setupCanvas();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setupCanvas]);


  const getCanvasPoint = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return {
      x: (clientX - rect.left) / rect.width * canvas.width,
      y: (clientY - rect.top) / rect.height * canvas.height
    };
  };

  const endDrawing = () => {
    if (toolMode === 'brush' && isDrawing) {
      setIsDrawing(false);
      lastPointRef.current = null;
      const canvas = canvasRef.current;
      if (canvas) {
        const mimeType = imageSrc.substring(imageSrc.indexOf(':') + 1, imageSrc.indexOf(';'));
        onDrawingComplete(canvas.toDataURL(mimeType));
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasPoint(e);
    if (!point) return;

    if (toolMode === 'brush') {
      setIsDrawing(true);
      lastPointRef.current = point;
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.fillStyle = getBrushColor();
        ctx.arc(point.x, point.y, brushSize / 2, 0, 2 * Math.PI);
        ctx.fill();
      }
      return;
    }

    if (toolMode === 'all') {
      // Re-trigger select all on click
      const canvas = canvasRef.current;
      if (canvas) {
        const w = canvas.width;
        const h = canvas.height;
        const allPoints = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        onLassoComplete(allPoints, { width: w, height: h });
      }
      return;
    }

    if (toolMode === 'freehand') {
      setIsDrawing(true);
      setPoints([point]);
    } else { // Polygonal logic
      if (!isDrawing) {
        // Start of a new selection, clear previous points
        setIsDrawing(true);
        setPoints([point]);
      } else {
        // Continue drawing current selection
        // Check for closing point
        if (points.length >= 3) {
          const firstPoint = points[0];
          const distance = Math.sqrt(Math.pow(firstPoint.x - point.x, 2) + Math.pow(firstPoint.y - point.y, 2));
          const canvas = canvasRef.current;
          const closingThreshold = canvas ? 15 / (canvas.getBoundingClientRect().width / canvas.width) : 15;
          if (distance < closingThreshold) {
            setIsDrawing(false);
            setCursorPos(null);
            onLassoComplete(points, { width: canvas!.width, height: canvas!.height });
            return;
          }
        }
        setPoints(prevPoints => [...prevPoints, point]);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) {
      // For polygonal tool, we want to show cursor preview even when not drawing a line, as long as there's a point
      if (toolMode === 'polygonal' && points.length > 0) {
        const currentPoint = getCanvasPoint(e);
        if (currentPoint) {
          setCursorPos(currentPoint);
        }
      }
      return;
    }
    const currentPoint = getCanvasPoint(e);
    if (!currentPoint) return;

    if (toolMode === 'brush') {
      if (lastPointRef.current) {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
          ctx.lineTo(currentPoint.x, currentPoint.y);
          ctx.strokeStyle = getBrushColor();
          ctx.lineWidth = brushSize;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
      }
      lastPointRef.current = currentPoint;
      return;
    }

    if (toolMode === 'freehand') {
      setPoints(prevPoints => [...prevPoints, currentPoint]);
    } else { // Polygonal logic
      setCursorPos(currentPoint);
    }
  };

  const handleMouseUp = () => {
    if (toolMode === 'brush') {
      endDrawing();
      return;
    }

    if (toolMode === 'freehand') {
      if (isDrawing && points.length > 2) {
        const canvas = canvasRef.current;
        if (canvas) {
          onLassoComplete(points, { width: canvas.width, height: canvas.height });
        }
      }
      setIsDrawing(false);
    }
    // For polygonal tool, mouse up does not end the drawing process
  };

  const handleMouseLeave = () => {
    if (toolMode === 'brush') {
      endDrawing();
      return;
    }
    if (toolMode === 'freehand') {
      handleMouseUp();
    } else {
      setCursorPos(null);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const point = getCanvasPoint(e);
    if (!point) return;

    if (toolMode === 'brush') {
      e.preventDefault();
      setIsDrawing(true);
      lastPointRef.current = point;
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.fillStyle = getBrushColor();
        ctx.arc(point.x, point.y, brushSize / 2, 0, 2 * Math.PI);
        ctx.fill();
      }
      return;
    }

    if (toolMode === 'freehand') {
      e.preventDefault();
      setIsDrawing(true);
      setPoints([point]);
      return;
    }

    if (toolMode === 'polygonal') {
      e.preventDefault();
      if (!isDrawing) {
        // Start of a new selection, clear previous points
        setIsDrawing(true);
        setPoints([point]);
      } else {
        // Continue drawing current selection
        // Check for closing point
        if (points.length >= 3) {
          const firstPoint = points[0];
          const distance = Math.sqrt(Math.pow(firstPoint.x - point.x, 2) + Math.pow(firstPoint.y - point.y, 2));
          const canvas = canvasRef.current;
          // Use a larger threshold for touch as it's less precise
          const closingThreshold = canvas ? 30 / (canvas.getBoundingClientRect().width / canvas.width) : 30;
          if (distance < closingThreshold) {
            setIsDrawing(false);
            setCursorPos(null);
            onLassoComplete(points, { width: canvas!.width, height: canvas!.height });
            return;
          }
        }
        setPoints(prevPoints => [...prevPoints, point]);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const currentPoint = getCanvasPoint(e);
    if (!currentPoint) return;

    if (toolMode === 'brush') {
      e.preventDefault();
      if (lastPointRef.current) {
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
          ctx.lineTo(currentPoint.x, currentPoint.y);
          ctx.strokeStyle = getBrushColor();
          ctx.lineWidth = brushSize;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
      }
      lastPointRef.current = currentPoint;
      return;
    }

    if (toolMode === 'freehand') {
      e.preventDefault();
      setPoints(prevPoints => [...prevPoints, currentPoint]);
    }
    // For polygonal, touch move does not add points
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (toolMode === 'brush') {
      endDrawing();
      return;
    }
    if (toolMode === 'freehand') {
      handleMouseUp(); // Reuse the same completion logic
    }
    // For polygonal, touch end does not complete the selection
  };

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="max-w-full max-h-full object-contain cursor-crosshair rounded-xl touch-none"
      />
    </div>
  );
};
